import { assign, createMachine } from "xstate";
import {
  ACTION_REQUIRED_ERROR,
  FastCheckoutMachineContext,
  FastCheckoutMachineEvents,
  FastCheckoutState,
} from "./types/FastCheckoutMachine";
import {
  getErrorTypeFromCheckoutOrder,
  getErrorStageNumberFromCheckoutSection,
  getActiveStageFromCheckoutSection,
} from "../utils";

export type FastCheckoutMachineType = typeof fastCheckoutMachine;

export const fastCheckoutMachine = (() => {
  const machineId = "fastCheckoutMachine";
  return createMachine(
    {
      id: machineId,
      initial: "waiting",
      types: {} as unknown as {
        context: FastCheckoutMachineContext;
        events: FastCheckoutMachineEvents;
      },
      on: {
        ON_SESSION_OPEN: {
          actions: ["openSection"],
        },
        ON_SESSION_CLOSE: {
          actions: ["closeSection"],
        },
        ON_CHECKOUT_STATUS_CHANGE: [
          {
            actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
            target: "#fastCheckoutMachine.processing",
            guard: "isCheckoutProcessingStage",
          },
          {
            actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
            target: "#fastCheckoutMachine.actionRequiredError",
            guard: "isCheckoutActionRequiredErrorStage",
          },
          {
            actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
            target: "#fastCheckoutMachine.nonActionRequiredError",
            guard: "isCheckoutNonActionRequiredErrorStage",
          },
          {
            actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
            target: "#fastCheckoutMachine.receiptGenerated",
            guard: "isCheckoutReceiptGeneratedStage",
          },
        ],
      },
      context: ({
        input: { context },
      }: {
        input: { context: FastCheckoutMachineContext };
      }) => context,
      states: {
        waiting: {
          entry: [
            assign({
              checkoutState: FastCheckoutState.LOADING,
              activeStage: 1,
            }),
          ],
        },
        processing: {
          entry: [
            assign({
              checkoutState: FastCheckoutState.PROCESSING,
            }),
          ],
        },
        actionRequiredError: {
          initial: ACTION_REQUIRED_ERROR.LOGIN_ERROR,
          entry: [
            assign({
              checkoutState: FastCheckoutState.ACTION_REQUIRED_ERROR,
            }),
          ],
          states: {
            [ACTION_REQUIRED_ERROR.LOGIN_ERROR]: {
              on: {
                SUBMIT_LOGIN_INFO: {
                  target: "#fastCheckoutMachine.processing",
                },
              },
            },
            [ACTION_REQUIRED_ERROR.OTP_ERROR]: {
              on: {
                SUBMIT_OTP: {
                  target: "#fastCheckoutMachine.processing",
                },
              },
            },
            [ACTION_REQUIRED_ERROR.OTP_TIMEOUT_ERROR]: {
              on: {
                OTP_TIME_OUT_RETRY: {
                  target: "#fastCheckoutMachine.processing",
                },
              },
            },
          },
        },
        nonActionRequiredError: {
          entry: assign({
            checkoutState: FastCheckoutState.NON_ACTION_REQUIRED_ERROR,
          }),
        },
        receiptGenerated: {
          entry: assign({
            checkoutState: FastCheckoutState.RECEIPT_GENERATED,
          }),
        },
      },
    },
    {
      actors: {},
      actions: {
        openSection: assign({
          sectionState: ({ event, context }) =>
            event?.type === "ON_SESSION_OPEN"
              ? {
                  ...context.sectionState,
                  [event.data.sessionToOpen]: {
                    ...context.sectionState[event.data.sessionToOpen],
                    open: true,
                  },
                }
              : context.sectionState,
        }),
        closeSection: assign({
          sectionState: ({ event, context }) =>
            event?.type === "ON_SESSION_CLOSE"
              ? {
                  ...context.sectionState,
                  [event.data.sessionToClose]: {
                    ...context.sectionState[event.data.sessionToClose],
                    open: false,
                  },
                }
              : context.sectionState,
        }),
        updateRetailerCheckoutOrderStatusFromPoller: assign({
          retailerCheckoutOrderStatus: ({ event, context }) =>
            event.type === "ON_CHECKOUT_STATUS_CHANGE"
              ? event.data.retailerCheckoutOrderStatus
              : context.retailerCheckoutOrderStatus,

          activeStage: ({ event, context }) => {
            if (event.type === "ON_CHECKOUT_STATUS_CHANGE") {
              const displayStage =
                event.data.retailerCheckoutOrderStatus?.displayStatusInfo
                  ?.stage;
              return getActiveStageFromCheckoutSection(displayStage);
            }
            return context.activeStage;
          },
          error: ({ event, context }) => {
            if (event.type === "ON_CHECKOUT_STATUS_CHANGE") {
              const order =
                event.data.retailerCheckoutOrderStatus
                  .userRetailerCheckoutOrder;
              const errorMessage =
                order?.userInteractionRequirement?.displayMessage;
              const errorType = getErrorTypeFromCheckoutOrder(order);
              if (!errorType) {
                return context.error;
              }
              return {
                stage: getErrorStageNumberFromCheckoutSection(
                  order?.userInteractionRequirement?.section
                ),
                type: errorType,
                interactionRequired: order?.userInteractionRequirement,
                errorMessage,
              };
            } else {
              return context.error;
            }
          },
        }),
      },
      guards: {
        isCheckoutProcessingStage: ({ context, event }) => {
          return true;
        },
        isCheckoutActionRequiredErrorStage: ({ context, event }) => {
          return false;
        },
        isCheckoutNonActionRequiredErrorStage: ({ context, event }) => {
          return false;
        },
        isCheckoutReceiptGeneratedStage: ({ context, event }) => {
          return false;
        },
      },
    }
  );
})();
